import ky, { HTTPError, type KyInstance, type Options } from 'ky';

// Vite 환경 변수에서 API URL 가져오기
const API_URL = import.meta.env.VITE_API_URL || 'https://dev-api-dk.surrodocs.ai';

type ApiErrorResponse = {
  status: string;
  code: number;
  data: {
    message: string;
  };
};

export async function getErrorMessage(
  error: unknown,
  defaultMessage = '알수 없는 오류가 발생했습니다. 잠시 후 다시 시도하시거나 관리자에게 문의해주세요.'
): Promise<string> {
  if (error instanceof HTTPError) {
    try {
      const errorData = await error.response.json<ApiErrorResponse>();
      return errorData?.data?.message || defaultMessage;
    } catch {
      return defaultMessage;
    }
  }
  return defaultMessage;
}

// 커스텀 에러 클래스
export class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const commonHooks = {
  beforeRequest: [(_request: Request) => {}],
  afterResponse: [
    async (_request: Request, _options: Options, response: Response) => {
      if (response.status === 401) {
        // 인증 만료 시 로그인 페이지로 리다이렉트
        if (typeof window !== 'undefined') {
          window.location.href = '/signin';
        }
      }
      return response;
    },
  ],
};

// 백엔드 직접 호출 클라이언트
const apiClient: KyInstance = ky.create({
  prefixUrl: API_URL,
  timeout: 60000,
  credentials: 'include', // 쿠키 전송을 위해 필요
  hooks: commonHooks,
});

const createApiHelpers = (client: KyInstance) => ({
  get: async <T>(url: string, options?: Options): Promise<T> => {
    return await client.get(url, options).json<T>();
  },

  post: async <T>(url: string, data?: unknown, options?: Options): Promise<T> => {
    const isFormData = data instanceof FormData;

    return await client
      .post(url, {
        ...(isFormData ? { body: data } : { json: data }),
        ...options,
      })
      .json<T>();
  },

  put: async <T>(url: string, data?: unknown, options?: Options): Promise<T> => {
    const isFormData = data instanceof FormData;

    return await client
      .put(url, {
        ...(isFormData ? { body: data } : { json: data }),
        ...options,
      })
      .json<T>();
  },

  patch: async <T>(url: string, data?: unknown, options?: Options): Promise<T> => {
    const isFormData = data instanceof FormData;

    return await client
      .patch(url, {
        ...(isFormData ? { body: data } : { json: data }),
        ...options,
      })
      .json<T>();
  },

  delete: async <T>(url: string, options?: Options): Promise<T> => {
    return await client.delete(url, options).json<T>();
  },
});

export const api = createApiHelpers(apiClient);

// 에러 타입 가드
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isHTTPError(error: unknown): error is HTTPError {
  return error instanceof HTTPError;
}
